import { BaseStore } from "../stores/base_store"
import { runInAction, makeAutoObservable } from "mobx"
import ClockAPI from "api/clock_api"
import { api_get } from "api/utils";
import { api_post } from "api/utils";

export type ClockProps = {
    key: string;
    uuid: string;
    identity: string;
    client_identity: string;
    user: string;


    associated_type: string;
    associated_uuid: string;
    time_start: string;
    time_end: string;
    variant: string;

    date_created: string;
    date_updated: string;
    updateProps?: (attributes: ClockProps) => void;
};

// Domain object Clock
export class Clock implements ClockProps {
    key = "";
    uuid = "";
    identity = "";
    client_identity = "";
    user = "";
    associated_type = "";
    associated_uuid = "";
    time_start = "";
    time_end = "";

    variant = "billable";
    date_created = "";
    date_updated = "";

    constructor(attributes: ClockProps) {
        makeAutoObservable(this, {
            updateProps: false
        })
        if (attributes) {
            this.updateProps(attributes);
        }
    }

    updateProps(attributes: ClockProps) {
       runInAction(() => {
            Object.assign(this, attributes);
        });
    }

}


export class ClockStore extends BaseStore<ClockProps, Clock> {
    CACHED_CLOCKS: { [key: string]: ClockProps } = {};

    // The current user's active clocks
    async fetchCurrentClocks() {
        return ClockAPI.listCurrent().then((resp) => {
            if (!resp) {
                return [];
            }
            this.receiveUpdate(resp.items);
            return resp.items as ClockProps[];
        });
    }

    async fetchClocksForCollab(collabKey: string, identityKey: string, cursor = "") {
        if(!collabKey)
            throw new Error("collabKey is required");
        if(!identityKey)
            throw new Error("identityKey is required");


        return ClockAPI.listByCollab(collabKey, identityKey, cursor).then((resp) => {
            if (!resp) {
                return [];
            }
            this.receiveUpdate(resp.items);
            if (resp.cursor) {
                this.fetchClocksForCollab(collabKey, identityKey, resp.cursor);
            }
            // This returns as possibly incomplete list due to the cursor,
            // but ClockStore.items is complete when .then() is called
            // so you can/should just use that.
            return resp.items;
        })
    }


    async fetchClock(identity: string, clock: string) {
        if(this.CACHED_CLOCKS[clock]) {
            console.log("fetchClock() cache hit");
            return this.CACHED_CLOCKS[clock];
        }
        return api_get("/clock/", { identity, clock }).then(async (res) => {
            if (!res.ok) {
                return null;
            }
            else {
                const c = await res.json();
                this.CACHED_CLOCKS[clock] = c;
                return c;
            }
        });
    }


    async fetchClocksForRequest(request: string, identity: string, cursor = "") {
        if(!request)
            throw new Error("collabKey is required");
        if(!identity)
            throw new Error("identityKey is required");

        return api_get("/clock/by-request", { request, identity, cursor })
            .then(async (res) => {
                if (!res.ok) {
                    return null;
                }
                else {
                    const c = await res.json();
                    this.receiveUpdate(c.items);
                    if (c.cursor) {
                        this.fetchClocksForRequest(request, identity, c.cursor);
                    }
                    return c.items;
                }
            });
    }


    async fetchClocksByAssociation(associated_type: string,
        associated_uuid: string, identity: string, cursor = "") {

        const params = {
            associated_type,
            associated_uuid,
            identity,
            cursor
        };
        return api_get("/clock/by-association", params).then(async (res) => {
            if (!res.ok) {
                return null;
            }
            else {
                const c = await res.json();
                this.receiveUpdate(c.items);
                return c.items;
            }
        });
        }

    async fetchClocksByClient(
        identity: string,
        client_identity: string,
        period_start: string,
        period_end: string,
        cursor = "") {

        const params = {
            identity,
            client_identity,
            period_start,
            period_end,
            cursor
        };
        return api_get("/clock/list/by-client", params).then(async (res) => {
            if (!res.ok) {
                return null;
            }
            else {
                const c = await res.json();
                this.receiveUpdate(c.items);
                return c;
            }
        });
    }

    async fetchClocksByAssistant(
        identity: string,
        assistant_identity: string,
        period_start: string,
        period_end: string,
        cursor = "") {

        const params = {
            identity,
            assistant_identity,
            period_start,
            period_end,
            cursor
        };
        return api_get("/clock/list/by-assistant", params).then(async (res) => {
            if (!res.ok) {
                return null;
            }
            else {
                const c = await res.json();
                this.receiveUpdate(c.items);
                return c;
            }
        });
    }

    async clockIn(
        identityKey: string,
        collab: string,
        request: string
    ) {
        if (collab && request)
            throw new Error("Cannot have collabKey and request at the same time");
        const data = await
            (collab ?
                ClockAPI.clockInCollab(identityKey, collab) :
                ClockAPI.clockInRequest(identityKey, request)
            );

        // _clockIn resolves to undefined on a non-OK response
        if (data)
            this.receiveUpdate(data);
        return data;
    }


    async clockOut(clockKey: string) {
        return ClockAPI.clockOut(clockKey).then((data) => {
            console.log("clockOut() %o", data);
            if (data?.clock)
                this.receiveUpdate(data.clock);
            return data;
        });
    }

    async editViaSupport(identity: string,
        clock: string,
        time_start: string,
        time_end: string,
        notes: string) {
        return api_post("/clock/edit-via-support",
            { identity, clock, time_start, time_end, notes })
            .then(async (res) => {
                const c = await res.json();
                this.receiveUpdate(c);
                return c;
            });
    }

    async createManualClock(
        identity: string,
        collab: string,
        time_start: string,
        time_end: string
        ) {
        return api_post("/clock/create-manual",
            { identity, associated_type: 'collab',  associated_uuid: collab, time_start, time_end })
            .then(async (res) => {
                const c = await res.json();
                this.receiveUpdate(c);
                return c;
            });
    }

    async resetTotals(identity: string,
        associated_type: string,
        associated_uuid: string
        ) {
        return api_post("/clock/reset-totals",
            { identity, associated_type, associated_uuid })
            .then(async (res) => {
                console.log("resetTotals() %o", res);
                return await res.json();
            });
    }

    async deleteViaSupport(identity: string, clock: string) {
        return api_post("/clock/delete-via-support",
            { identity, clock })
            .then(async (res) => {
                console.log("deleteViaSupport() %o", res);
                return await res.json();
            });
    }

    modelConstructor() {
        return Clock;
    }

}
